import React, { useState, useMemo, useEffect, useRef } from "react";
import { Card, Row, Col, Form, Button, Alert, Modal, Badge, InputGroup, Spinner, OverlayTrigger, Tooltip } from "react-bootstrap";
import Footer from "../layouts/Footer";

export default function ScheduledMessageSender() {
  // ============================================
  // ÉTATS
  // ============================================
  const [messageData, setMessageData] = useState({
    texte: "",
    destinataires: "",
    type: "SMS Pro",
    dateEnvoi: "",
    heureEnvoi: "",
    reference: "",
    options: {
      accuséReception: false,
      messageLong: false,
      personnalisation: false
    }
  });

  const [currentStep, setCurrentStep] = useState("form");
  const [showFormModal, setShowFormModal] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const [filterText, setFilterText] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortField, setSortField] = useState('dateEnvoiComplet');
  const [sortDirection, setSortDirection] = useState('desc');

  const [pendingMessages, setPendingMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);

  const [scheduledMessages, setScheduledMessages] = useState([]);

  const initDoneRef = useRef(false);

  // ============================================
  // AUTH
  // ============================================
  const getAuthToken = () => {
    return localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
  };

  // ============================================
  // CHARGEMENT
  // ============================================
  useEffect(() => {
    if (initDoneRef.current) return;
    initDoneRef.current = true;
    fetchPendingMessages();
  }, []);

  const fetchPendingMessages = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = getAuthToken();
      if (!token) throw new Error('Token d\'authentification manquant');

      const response = await fetch('/pending-sms-model', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        }
      });

      if (!response.ok) throw new Error(`Erreur HTTP: ${response.status}`);

      const result = await response.json();

      if (result.code === "200" && Array.isArray(result.data)) {
        setPendingMessages(result.data);

        const transformedMessages = result.data.map((msg, index) => ({
          id: msg.id,
          texte: msg.processed_message || msg.message,
          destinataires: msg.msisdn,
          type: msg.type === "pro" ? "SMS Pro" : "SMS Standard",
          sender_id: msg.sender_id,
          dateEnvoiComplet: new Date(Date.now() + (index * 24 * 60 * 60 * 1000)).toLocaleString('fr-FR'),
          statut: getStatusText(msg.status),
          dateCreation: new Date(Date.now() - (index * 2 * 60 * 60 * 1000)).toLocaleString('fr-FR'),
          originalData: msg
        }));

        setScheduledMessages(transformedMessages);
      } else {
        throw new Error('Format de réponse invalide');
      }
    } catch (err) {
      console.error('Erreur chargement:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // HELPERS
  // ============================================
  const getStatusText = (status) => {
    const map = {
      'pending': 'En attente',
      'processed': 'Traité',
      'failed': 'Échec',
      'Programmé': 'Programmé'
    };
    return map[status] || status;
  };

  const getStatusVariant = (status) => {
    const map = {
      'pending': 'warning',
      'processed': 'success',
      'failed': 'danger',
      'Programmé': 'info',
      'En attente': 'warning',
      'Traité': 'success',
      'Échec': 'danger'
    };
    return map[status] || 'secondary';
  };

  const getStatusIcon = (status) => {
    const map = {
      'pending': 'ri-time-line',
      'processed': 'ri-checkbox-circle-fill',
      'failed': 'ri-close-circle-fill',
      'Programmé': 'ri-calendar-check-fill',
      'En attente': 'ri-time-line',
      'Traité': 'ri-checkbox-circle-fill',
      'Échec': 'ri-close-circle-fill'
    };
    return map[status] || 'ri-question-line';
  };

  const getStatusColors = (status) => {
    const map = {
      'pending': { color: '#f59e0b', bg: '#fef3c7' },
      'En attente': { color: '#f59e0b', bg: '#fef3c7' },
      'processed': { color: '#10b981', bg: '#d1fae5' },
      'Traité': { color: '#10b981', bg: '#d1fae5' },
      'failed': { color: '#ef4444', bg: '#fee2e2' },
      'Échec': { color: '#ef4444', bg: '#fee2e2' },
      'Programmé': { color: '#3b82f6', bg: '#eff6ff' }
    };
    return map[status] || { color: '#6b7280', bg: '#f3f4f6' };
  };

  // ============================================
  // FILTRAGE ET TRI
  // ============================================
  const filteredAndSortedItems = useMemo(() => {
    let filtered = scheduledMessages.filter(item => {
      const matchText = !filterText ||
        item.texte.toLowerCase().includes(filterText.toLowerCase()) ||
        item.destinataires.includes(filterText) ||
        item.type.toLowerCase().includes(filterText.toLowerCase()) ||
        item.statut.toLowerCase().includes(filterText.toLowerCase()) ||
        item.sender_id.toLowerCase().includes(filterText.toLowerCase());

      const matchStatus = filterStatus === 'all' || item.statut === filterStatus;

      return matchText && matchStatus;
    });

    filtered.sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      if (sortField === 'dateEnvoiComplet') {
        aValue = new Date(aValue.split('/').reverse().join('-'));
        bValue = new Date(bValue.split('/').reverse().join('-'));
      }

      if (sortDirection === 'asc') return aValue > bValue ? 1 : -1;
      return aValue < bValue ? 1 : -1;
    });

    return filtered;
  }, [scheduledMessages, filterText, filterStatus, sortField, sortDirection]);

  // ============================================
  // PAGINATION
  // ============================================
  const totalPages = Math.ceil(filteredAndSortedItems.length / itemsPerPage) || 1;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = filteredAndSortedItems.slice(startIndex, startIndex + itemsPerPage);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // ============================================
  // STATS
  // ============================================
  const stats = useMemo(() => {
    const total = scheduledMessages.length;
    const pending = scheduledMessages.filter(m => m.statut === "En attente" || m.statut === "pending").length;
    const processed = scheduledMessages.filter(m => m.statut === "Traité" || m.statut === "processed").length;
    const failed = scheduledMessages.filter(m => m.statut === "Échec" || m.statut === "failed").length;
    return { total, pending, processed, failed };
  }, [scheduledMessages]);

  // ============================================
  // HANDLERS FORMULAIRE
  // ============================================
  const handleInputChange = (field, value) => {
    setMessageData(prev => ({ ...prev, [field]: value }));
  };

  const handleOptionChange = (option, value) => {
    setMessageData(prev => ({
      ...prev,
      options: { ...prev.options, [option]: value }
    }));
  };

  const calculateCost = () => {
    const baseCost = messageData.type === "SMS Pro" ? 4.8 : 3.2;
    const optionsCost = messageData.options.messageLong ? 1.5 : 0;
    const recipientsCount = messageData.destinataires
      ? messageData.destinataires.split(/[,\n]/).filter(r => r.trim()).length : 0;
    return (baseCost + optionsCost) * recipientsCount;
  };

  const validateForm = () => {
    return (
      messageData.texte.trim() !== "" &&
      messageData.destinataires.trim() !== "" &&
      messageData.dateEnvoi !== "" &&
      messageData.heureEnvoi !== ""
    );
  };

  const getMinDate = () => new Date().toISOString().split('T')[0];

  const scheduleMessage = async () => {
    if (!validateForm()) {
      alert("Veuillez remplir tous les champs obligatoires");
      return;
    }

    try {
      const newMessage = {
        id: Date.now(),
        ...messageData,
        dateEnvoiComplet: `${messageData.dateEnvoi} ${messageData.heureEnvoi}`,
        statut: "Programmé",
        dateCreation: new Date().toLocaleString('fr-FR'),
        originalData: {
          type: messageData.type === "SMS Pro" ? "pro" : "standard",
          status: "pending"
        }
      };

      setScheduledMessages(prev => [newMessage, ...prev]);
      setSuccessMessage("Message programmé avec succès !");
      setShowSuccess(true);
      setShowFormModal(false);
      setCurrentStep("form");

      setMessageData({
        texte: "", destinataires: "", type: "SMS Pro",
        dateEnvoi: "", heureEnvoi: "", reference: "",
        options: { accuséReception: false, messageLong: false, personnalisation: false }
      });

      setTimeout(() => setShowSuccess(false), 5000);
    } catch (err) {
      console.error('Erreur programmation:', err);
      alert('Erreur lors de la programmation');
    }
  };

  // ============================================
  // MODALES
  // ============================================
  const openDetailModal = (message) => {
    setSelectedMessage(message);
    setShowDetailModal(true);
  };

  const openDeleteModal = (message) => {
    setSelectedMessage(message);
    setShowDeleteModal(true);
  };

  const cancelScheduledMessage = async () => {
    try {
      setScheduledMessages(prev => prev.filter(msg => msg.id !== selectedMessage.id));
      setShowDeleteModal(false);
      setSelectedMessage(null);
      setSuccessMessage("Message supprimé avec succès !");
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 5000);
    } catch (err) {
      console.error('Erreur suppression:', err);
      alert('Erreur lors de la suppression');
    }
  };

  const openNewMessageModal = () => {
    setMessageData({
      texte: "", destinataires: "", type: "SMS Pro",
      dateEnvoi: "", heureEnvoi: "", reference: "",
      options: { accuséReception: false, messageLong: false, personnalisation: false }
    });
    setCurrentStep("form");
    setShowFormModal(true);
  };

  const goToPreview = () => {
    if (validateForm()) setCurrentStep("preview");
  };

  const goToForm = () => setCurrentStep("form");

  const refreshData = () => fetchPendingMessages();

  // ============================================
  // SUB-COMPONENTS
  // ============================================
  const SortableHeader = ({ field, children }) => (
    <th className="sch-sortable-header" onClick={() => handleSort(field)}>
      <span className="sch-sortable-content">
        {children}
        {sortField === field && (
          <i className={`ri-arrow-${sortDirection === 'asc' ? 'up' : 'down'}-line sch-sort-icon`}></i>
        )}
      </span>
    </th>
  );

  // ============================================
  // RENDER : FORM STEP
  // ============================================
  const renderFormStep = () => (
    <Row className="g-4">
      <Col lg={8}>
        <div className="sch-form-card">
          <div className="sch-form-header">
            <div className="d-flex align-items-center gap-2">
              <div className="sch-form-icon">
                <i className="ri-edit-box-line"></i>
              </div>
              <div>
                <h6 className="sch-form-title mb-0">Détails du message</h6>
                <small className="sch-form-subtitle">Remplissez les informations ci-dessous</small>
              </div>
            </div>
          </div>

          <div className="sch-form-body">
            {/* Type de message */}
            <div className="sch-section">
              <label className="sch-label">
                <span className="sch-label-number">1</span>
                Type de message <span className="sch-required">*</span>
              </label>
              <div className="row g-3">
                <div className="col-md-6">
                  <label className={`sch-option-card ${messageData.type === "SMS Pro" ? 'active' : ''}`}>
                    <input
                      type="radio"
                      name="messageType"
                      value="SMS Pro"
                      checked={messageData.type === "SMS Pro"}
                      onChange={(e) => handleInputChange('type', e.target.value)}
                    />
                    <div className="sch-option-icon sch-option-icon-amber">
                      <i className="ri-star-fill"></i>
                    </div>
                    <div className="sch-option-content">
                      <div className="sch-option-title">SMS Pro</div>
                      <div className="sch-option-desc">Haute priorité, accusé de lecture</div>
                    </div>
                    <div className="sch-option-check"><i className="ri-checkbox-circle-fill"></i></div>
                  </label>
                </div>
                <div className="col-md-6">
                  <label className={`sch-option-card ${messageData.type === "SMS Standard" ? 'active' : ''}`}>
                    <input
                      type="radio"
                      name="messageType"
                      value="SMS Standard"
                      checked={messageData.type === "SMS Standard"}
                      onChange={(e) => handleInputChange('type', e.target.value)}
                    />
                    <div className="sch-option-icon sch-option-icon-blue">
                      <i className="ri-chat-3-line"></i>
                    </div>
                    <div className="sch-option-content">
                      <div className="sch-option-title">SMS Standard</div>
                      <div className="sch-option-desc">Usage général, économique</div>
                    </div>
                    <div className="sch-option-check"><i className="ri-checkbox-circle-fill"></i></div>
                  </label>
                </div>
              </div>
            </div>

            {/* Destinataires */}
            <div className="sch-section">
              <label className="sch-label">
                <span className="sch-label-number">2</span>
                Destinataires <span className="sch-required">*</span>
              </label>
              <textarea
                className="sch-textarea"
                rows={3}
                placeholder="Saisissez les numéros (séparés par des virgules ou retours à la ligne)"
                value={messageData.destinataires}
                onChange={(e) => handleInputChange('destinataires', e.target.value)}
              />
              <div className="sch-textarea-footer">
                <span className="sch-hint">
                  <i className="ri-information-line"></i>
                  {messageData.destinataires
                    ? `${messageData.destinataires.split(/[,\n]/).filter(r => r.trim()).length} destinataire(s) détecté(s)`
                    : 'Saisissez au moins un numéro'}
                </span>
                {messageData.destinataires && (
                  <span className="sch-counter sch-counter-primary">
                    <i className="ri-user-line"></i>
                    {messageData.destinataires.split(/[,\n]/).filter(r => r.trim()).length}
                  </span>
                )}
              </div>
            </div>

            {/* Contenu */}
            <div className="sch-section">
              <label className="sch-label">
                <span className="sch-label-number">3</span>
                Contenu du message <span className="sch-required">*</span>
              </label>
              <textarea
                className="sch-textarea"
                rows={4}
                placeholder="Rédigez votre message SMS ici..."
                value={messageData.texte}
                onChange={(e) => handleInputChange('texte', e.target.value)}
                maxLength={messageData.options.messageLong ? 459 : 160}
              />
              <div className="sch-textarea-footer">
                <div className="sch-char-counter">
                  <div className="sch-char-progress">
                    <div
                      className={`sch-char-progress-bar ${messageData.texte.length > (messageData.options.messageLong ? 459 : 160) * 0.8 ? 'warning' : ''}`}
                      style={{ width: `${Math.min((messageData.texte.length / (messageData.options.messageLong ? 459 : 160)) * 100, 100)}%` }}
                    ></div>
                  </div>
                  <span className={`sch-char-text ${messageData.texte.length > 160 && !messageData.options.messageLong ? 'warning' : ''}`}>
                    {messageData.texte.length} / {messageData.options.messageLong ? 459 : 160}
                  </span>
                </div>
                {messageData.texte.length > 160 && !messageData.options.messageLong && (
                  <span className="sch-warning">
                    <i className="ri-alert-line"></i>
                    Sera divisé en plusieurs SMS
                  </span>
                )}
              </div>
            </div>

            {/* Date & Heure */}
            <div className="sch-section">
              <label className="sch-label">
                <span className="sch-label-number">4</span>
                Programmation <span className="sch-required">*</span>
              </label>
              <div className="row g-3">
                <div className="col-md-6">
                  <div className="sch-input-wrapper">
                    <i className="ri-calendar-line sch-input-icon"></i>
                    <input
                      type="date"
                      className="sch-input"
                      min={getMinDate()}
                      value={messageData.dateEnvoi}
                      onChange={(e) => handleInputChange('dateEnvoi', e.target.value)}
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="sch-input-wrapper">
                    <i className="ri-time-line sch-input-icon"></i>
                    <input
                      type="time"
                      className="sch-input"
                      value={messageData.heureEnvoi}
                      onChange={(e) => handleInputChange('heureEnvoi', e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Référence */}
            <div className="sch-section">
              <label className="sch-label">
                <span className="sch-label-number">5</span>
                Référence <small className="sch-optional">(optionnel)</small>
              </label>
              <input
                type="text"
                className="sch-input-full"
                placeholder="Ex: CAMPAGNE_MARKETING_OCT_2025"
                value={messageData.reference}
                onChange={(e) => handleInputChange('reference', e.target.value)}
              />
              <div className="sch-hint mt-2">
                <i className="ri-tag-line"></i>
                Identifiant unique pour retrouver facilement ce message
              </div>
            </div>

            {/* Options */}
            <div className="sch-section">
              <label className="sch-label">
                <span className="sch-label-number">6</span>
                Options avancées
              </label>
              <div className="sch-options-grid">
                <label className={`sch-option-checkbox ${messageData.options.accuséReception ? 'active' : ''}`}>
                  <input
                    type="checkbox"
                    checked={messageData.options.accuséReception}
                    onChange={(e) => handleOptionChange('accuséReception', e.target.checked)}
                  />
                  <div className="sch-option-checkbox-icon">
                    <i className="ri-check-double-line"></i>
                  </div>
                  <div>
                    <div className="sch-option-checkbox-title">Accusé de réception</div>
                    <div className="sch-option-checkbox-desc">Confirmation de lecture</div>
                  </div>
                </label>

                <label className={`sch-option-checkbox ${messageData.options.messageLong ? 'active' : ''}`}>
                  <input
                    type="checkbox"
                    checked={messageData.options.messageLong}
                    onChange={(e) => handleOptionChange('messageLong', e.target.checked)}
                  />
                  <div className="sch-option-checkbox-icon">
                    <i className="ri-file-text-line"></i>
                  </div>
                  <div>
                    <div className="sch-option-checkbox-title">Message long</div>
                    <div className="sch-option-checkbox-desc">Concaténation SMS</div>
                  </div>
                </label>

                <label className={`sch-option-checkbox ${messageData.options.personnalisation ? 'active' : ''}`}>
                  <input
                    type="checkbox"
                    checked={messageData.options.personnalisation}
                    onChange={(e) => handleOptionChange('personnalisation', e.target.checked)}
                  />
                  <div className="sch-option-checkbox-icon">
                    <i className="ri-magic-line"></i>
                  </div>
                  <div>
                    <div className="sch-option-checkbox-title">Personnalisation</div>
                    <div className="sch-option-checkbox-desc">Variables dynamiques</div>
                  </div>
                </label>
              </div>
            </div>
          </div>
        </div>
      </Col>

      {/* Sidebar résumé */}
      <Col lg={4}>
        <div className="sch-sidebar sticky-top" style={{ top: '20px', zIndex: 10 }}>
          <div className="sch-summary-card">
            <div className="sch-summary-header">
              <div className="d-flex align-items-center gap-2">
                <div className="sch-summary-icon">
                  <i className="ri-file-list-3-line"></i>
                </div>
                <div>
                  <h6 className="sch-summary-title mb-0">Récapitulatif</h6>
                  <small className="sch-summary-subtitle">Vérifiez avant programmation</small>
                </div>
              </div>
            </div>

            <div className="sch-summary-body">
              <div className="sch-summary-item">
                <span className="sch-summary-label">
                  <i className="ri-price-tag-3-line"></i>
                  Type
                </span>
                <span className={`sch-summary-badge ${messageData.type === "SMS Pro" ? "amber" : "blue"}`}>
                  {messageData.type}
                </span>
              </div>

              <div className="sch-summary-item">
                <span className="sch-summary-label">
                  <i className="ri-user-line"></i>
                  Destinataires
                </span>
                <span className="sch-summary-value sch-summary-value-highlight">
                  {messageData.destinataires
                    ? messageData.destinataires.split(/[,\n]/).filter(r => r.trim()).length
                    : 0}
                </span>
              </div>

              <div className="sch-summary-item">
                <span className="sch-summary-label">
                  <i className="ri-text"></i>
                  Caractères
                </span>
                <span className={`sch-summary-value ${messageData.texte.length > 160 ? 'text-warning' : ''}`}>
                  {messageData.texte.length}
                </span>
              </div>

              <div className="sch-summary-divider"></div>

              <div className="sch-summary-date">
                <div className="sch-summary-date-label">
                  <i className="ri-calendar-check-line"></i>
                  Envoi programmé
                </div>
                {messageData.dateEnvoi && messageData.heureEnvoi ? (
                  <div className="sch-summary-date-value">
                    <div className="sch-summary-date-day">{messageData.dateEnvoi}</div>
                    <div className="sch-summary-date-time">à {messageData.heureEnvoi}</div>
                  </div>
                ) : (
                  <div className="sch-summary-date-empty">
                    Non définie
                  </div>
                )}
              </div>

              <div className="sch-summary-divider"></div>

              <div className="sch-summary-cost">
                <span className="sch-summary-cost-label">Coût estimé</span>
                <span className="sch-summary-cost-value">
                  {calculateCost().toFixed(1)} <small>€</small>
                </span>
              </div>
            </div>

            <div className="sch-summary-footer">
              <button
                className="sch-btn-secondary w-100"
                onClick={goToPreview}
                disabled={!validateForm()}
              >
                <i className="ri-eye-line"></i>
                Aperçu du message
              </button>

              <button
                className="sch-btn-primary w-100"
                onClick={scheduleMessage}
                disabled={!validateForm()}
              >
                <i className="ri-calendar-check-fill"></i>
                Programmer l'envoi
              </button>

              <div className="sch-summary-hint">
                <i className="ri-shield-check-line"></i>
                Envoi automatique à la date programmée
              </div>
            </div>
          </div>
        </div>
      </Col>
    </Row>
  );

  // ============================================
  // RENDER : PREVIEW STEP
  // ============================================
  const renderPreviewStep = () => (
    <div className="sch-preview">
      <button className="sch-btn-secondary mb-4" onClick={goToForm}>
        <i className="ri-arrow-left-line"></i>
        Retour au formulaire
      </button>

      <div className="sch-preview-header">
        <div className="sch-preview-header-icon">
          <i className="ri-eye-line"></i>
        </div>
        <div>
          <h4 className="sch-preview-header-title mb-0">Aperçu du message</h4>
          <p className="sch-preview-header-subtitle mb-0">Vérifiez les détails avant programmation</p>
        </div>
      </div>

      {/* Stats rapides */}
      <Row className="g-3 mb-4">
        <Col md={4}>
          <div className="sch-preview-stat">
            <div className="sch-preview-stat-icon sch-preview-stat-icon-blue">
              <i className="ri-chat-3-line"></i>
            </div>
            <div className="sch-preview-stat-value">{messageData.type}</div>
            <div className="sch-preview-stat-label">Type</div>
          </div>
        </Col>
        <Col md={4}>
          <div className="sch-preview-stat">
            <div className="sch-preview-stat-icon sch-preview-stat-icon-green">
              <i className="ri-user-line"></i>
            </div>
            <div className="sch-preview-stat-value">
              {messageData.destinataires ? messageData.destinataires.split(/[,\n]/).filter(r => r.trim()).length : 0}
            </div>
            <div className="sch-preview-stat-label">Destinataires</div>
          </div>
        </Col>
        <Col md={4}>
          <div className="sch-preview-stat">
            <div className="sch-preview-stat-icon sch-preview-stat-icon-amber">
              <i className="ri-money-euro-circle-line"></i>
            </div>
            <div className="sch-preview-stat-value text-success">{calculateCost().toFixed(1)} €</div>
            <div className="sch-preview-stat-label">Coût estimé</div>
          </div>
        </Col>
      </Row>

      {/* Programmation */}
      <div className="sch-preview-section">
        <div className="sch-preview-section-header">
          <i className="ri-calendar-check-line"></i>
          <span>Programmation</span>
        </div>
        <div className="row g-3">
          <div className="col-md-6">
            <div className="sch-preview-row">
              <span className="sch-preview-row-label">Date d'envoi</span>
              <span className="sch-preview-row-value">{messageData.dateEnvoi}</span>
            </div>
          </div>
          <div className="col-md-6">
            <div className="sch-preview-row">
              <span className="sch-preview-row-label">Heure d'envoi</span>
              <span className="sch-preview-row-value">{messageData.heureEnvoi}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Options */}
      <div className="sch-preview-section">
        <div className="sch-preview-section-header">
          <i className="ri-settings-3-line"></i>
          <span>Options activées</span>
        </div>
        {Object.entries(messageData.options).filter(([_, v]) => v).length > 0 ? (
          <div className="row g-2">
            {Object.entries(messageData.options)
              .filter(([_, value]) => value)
              .map(([key]) => (
                <div key={key} className="col-md-4">
                  <div className="sch-preview-option">
                    <i className="ri-checkbox-circle-fill"></i>
                    <span className="text-capitalize">
                      {key.replace(/([A-Z])/g, ' $1').trim()}
                    </span>
                  </div>
                </div>
              ))}
          </div>
        ) : (
          <div className="sch-preview-empty">
            <i className="ri-information-line"></i>
            Aucune option activée
          </div>
        )}
      </div>

      {/* Destinataires */}
      <div className="sch-preview-section">
        <div className="sch-preview-section-header">
          <i className="ri-user-line"></i>
          <span>Destinataires</span>
        </div>
        {messageData.destinataires ? (
          <div className="sch-preview-recipients">
            {messageData.destinataires.split(/[,\n]/).map((dest, index) => (
              dest.trim() && (
                <div key={index} className="sch-preview-recipient">
                  <i className="ri-phone-line"></i>
                  <code>{dest.trim()}</code>
                </div>
              )
            ))}
          </div>
        ) : (
          <div className="sch-preview-empty">
            <i className="ri-user-unfollow-line"></i>
            Aucun destinataire saisi
          </div>
        )}
      </div>

      {/* Message */}
      <div className="sch-preview-section">
        <div className="sch-preview-section-header">
          <i className="ri-chat-quote-line"></i>
          <span>Contenu du message</span>
        </div>
        <div className="sch-preview-message">
          <div className="sch-preview-message-label">SIMULATION SMS</div>
          <div className="sch-preview-message-content">
            {messageData.texte || "(Aucun contenu saisi)"}
          </div>
          <div className="sch-preview-message-footer">
            {messageData.texte.length}/{messageData.options.messageLong ? 459 : 160}
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="sch-preview-actions">
        <div className="sch-preview-actions-info">
          <i className="ri-shield-check-line"></i>
          Prêt à être programmé
        </div>
        <div className="sch-preview-actions-buttons">
          <button className="sch-btn-secondary" onClick={goToForm}>
            <i className="ri-pencil-line"></i>
            Modifier
          </button>
          <button className="sch-btn-primary" onClick={scheduleMessage}>
            <i className="ri-calendar-check-fill"></i>
            Programmer
          </button>
        </div>
      </div>
    </div>
  );

  // ============================================
  // RENDER PRINCIPAL
  // ============================================
  return (
    <div className="sch-page main main-app p-3 p-lg-4">
      {/* ========== SUCCESS ALERT ========== */}
      {showSuccess && (
        <div className="sch-alert sch-alert-success mb-4">
          <i className="ri-checkbox-circle-fill"></i>
          <div className="flex-grow-1">{successMessage}</div>
          <button className="sch-alert-close" onClick={() => setShowSuccess(false)}>
            <i className="ri-close-line"></i>
          </button>
        </div>
      )}

      {/* ========== HEADER ========== */}
      <div className="sch-header mb-4">
        <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
          <div className="d-flex align-items-center gap-3">
            <div className="sch-header-icon">
              <i className="ri-time-line"></i>
            </div>
            <div>
              <h3 className="sch-header-title mb-1">Modèles en cours</h3>
              <p className="sch-header-subtitle mb-0">
                Suivez et gérez vos messages en attente d'envoi
              </p>
            </div>
          </div>
          <div className="d-flex gap-2">
            <Button className="sch-btn-secondary" onClick={refreshData} disabled={loading}>
              <i className={`ri-refresh-line ${loading ? 'spinning' : ''}`}></i>
              <span>Actualiser</span>
            </Button>
          
          </div>
        </div>
      </div>

      {/* ========== STATS ========== */}
      <Row className="g-3 mb-4">
        <Col sm={6} lg={3}>
          <div className="sch-stat-card sch-stat-blue">
            <div className="sch-stat-icon-wrapper sch-stat-icon-blue">
              <i className="ri-file-list-3-line"></i>
            </div>
            <div className="sch-stat-content">
              <div className="sch-stat-label">Total</div>
              <div className="sch-stat-value">{stats.total.toLocaleString('fr-FR')}</div>
            </div>
          </div>
        </Col>
        <Col sm={6} lg={3}>
          <div className="sch-stat-card sch-stat-amber">
            <div className="sch-stat-icon-wrapper sch-stat-icon-amber">
              <i className="ri-time-line"></i>
            </div>
            <div className="sch-stat-content">
              <div className="sch-stat-label">En attente</div>
              <div className="sch-stat-value">{stats.pending.toLocaleString('fr-FR')}</div>
            </div>
          </div>
        </Col>
        <Col sm={6} lg={3}>
          <div className="sch-stat-card sch-stat-green">
            <div className="sch-stat-icon-wrapper sch-stat-icon-green">
              <i className="ri-checkbox-circle-line"></i>
            </div>
            <div className="sch-stat-content">
              <div className="sch-stat-label">Traités</div>
              <div className="sch-stat-value">{stats.processed.toLocaleString('fr-FR')}</div>
            </div>
          </div>
        </Col>
        <Col sm={6} lg={3}>
          <div className="sch-stat-card sch-stat-red">
            <div className="sch-stat-icon-wrapper sch-stat-icon-red">
              <i className="ri-close-circle-line"></i>
            </div>
            <div className="sch-stat-content">
              <div className="sch-stat-label">Échecs</div>
              <div className="sch-stat-value">{stats.failed.toLocaleString('fr-FR')}</div>
            </div>
          </div>
        </Col>
      </Row>

      {/* ========== FILTRES ========== */}
      <div className="sch-filters mb-4">
        <Row className="g-3 align-items-center">
          <Col lg={6} md={12}>
            <div className="sch-search-wrapper">
              <i className="ri-search-line sch-search-icon"></i>
              <input
                type="text"
                className="sch-search-input"
                placeholder="Rechercher un message, destinataire, sender..."
                value={filterText}
                onChange={(e) => { setFilterText(e.target.value); setCurrentPage(1); }}
              />
              {filterText && (
                <button className="sch-search-clear" onClick={() => setFilterText('')}>
                  <i className="ri-close-line"></i>
                </button>
              )}
            </div>
          </Col>
          <Col lg={3} md={6}>
            <select
              className="sch-select"
              value={filterStatus}
              onChange={(e) => { setFilterStatus(e.target.value); setCurrentPage(1); }}
            >
              <option value="all">Tous les statuts</option>
              <option value="En attente">⏳ En attente</option>
              <option value="Traité">✅ Traités</option>
              <option value="Échec">❌ Échecs</option>
              <option value="Programmé">📅 Programmés</option>
            </select>
          </Col>
          <Col lg={3} md={6}>
            <select
              className="sch-select"
              value={itemsPerPage}
              onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
            >
              <option value={5}>5 par page</option>
              <option value={10}>10 par page</option>
              <option value={25}>25 par page</option>
              <option value={50}>50 par page</option>
            </select>
          </Col>
        </Row>
      </div>

      {/* ========== TABLE ========== */}
      <div className="sch-table-card">
        {loading ? (
          <div className="sch-loading">
            <div className="sch-loading-spinner"></div>
            <div className="sch-loading-text">Chargement des messages...</div>
          </div>
        ) : error ? (
          <div className="sch-error-state">
            <i className="ri-error-warning-line"></i>
            <h5 className="sch-error-title">Erreur de chargement</h5>
            <p className="sch-error-text">{error}</p>
            <button className="sch-btn-primary" onClick={refreshData}>
              <i className="ri-refresh-line"></i>
              Réessayer
            </button>
          </div>
        ) : (
          <>
            <div className="table-responsive">
              <table className="sch-table">
                <thead>
                  <tr>
                    <SortableHeader field="texte">
                      <i className="ri-file-text-line me-1"></i>Message
                    </SortableHeader>
                    <SortableHeader field="destinataires">
                      <i className="ri-user-line me-1"></i>Destinataire
                    </SortableHeader>
                    <SortableHeader field="sender_id">
                      <i className="ri-at-line me-1"></i>Sender
                    </SortableHeader>
                    <SortableHeader field="type">
                      <i className="ri-price-tag-3-line me-1"></i>Type
                    </SortableHeader>
                    <SortableHeader field="statut">
                      <i className="ri-flag-line me-1"></i>Statut
                    </SortableHeader>
                    <th className="text-end" style={{ width: '120px' }}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.length > 0 ? (
                    currentItems.map((msg) => {
                      const statusColors = getStatusColors(msg.statut);
                      return (
                        <tr key={msg.id} className="sch-row">
                          <td>
                            <div className="sch-message-cell">
                              <div className="sch-message-text" title={msg.texte}>
                                {msg.texte.length > 60 ? `${msg.texte.substring(0, 60)}...` : msg.texte}
                              </div>
                              <div className="sch-message-meta">
                                <i className="ri-calendar-line"></i>
                                {msg.dateCreation}
                              </div>
                            </div>
                          </td>
                          <td>
                            <code className="sch-code">{msg.destinataires}</code>
                          </td>
                          <td>
                            <span className="sch-badge sch-badge-info">
                              <i className="ri-at-line"></i>
                              {msg.sender_id}
                            </span>
                          </td>
                          <td>
                            <span className="sch-badge sch-badge-light">
                              {msg.type}
                            </span>
                          </td>
                          <td>
                            <span
                              className="sch-status-badge"
                              style={{ color: statusColors.color, backgroundColor: statusColors.bg }}
                            >
                              <i className={getStatusIcon(msg.statut)}></i>
                              {msg.statut}
                            </span>
                          </td>
                          <td className="text-end">
                            <div className="sch-actions">
                              <OverlayTrigger placement="top" overlay={<Tooltip>Voir détails</Tooltip>}>
                                <button
                                  className="sch-action-btn sch-action-view"
                                  onClick={() => openDetailModal(msg)}
                                >
                                  <i className="ri-eye-line"></i>
                                </button>
                              </OverlayTrigger>
                              <OverlayTrigger placement="top" overlay={<Tooltip>Annuler</Tooltip>}>
                                <button
                                  className="sch-action-btn sch-action-delete"
                                  onClick={() => openDeleteModal(msg)}
                                >
                                  <i className="ri-delete-bin-line"></i>
                                </button>
                              </OverlayTrigger>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="6">
                        <div className="sch-empty-state">
                          <div className="sch-empty-icon">
                            <i className="ri-inbox-line"></i>
                          </div>
                          <h5 className="sch-empty-title">Aucun message trouvé</h5>
                          <p className="sch-empty-text">
                            {filterText || filterStatus !== 'all'
                              ? "Aucun message ne correspond à vos critères"
                              : "Aucun modèle en cours d'envoi"}
                          </p>
                          {(filterText || filterStatus !== 'all') && (
                            <button
                              className="sch-btn-empty-reset"
                              onClick={() => { setFilterText(''); setFilterStatus('all'); }}
                            >
                              <i className="ri-refresh-line me-2"></i>
                              Réinitialiser les filtres
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {filteredAndSortedItems.length > 0 && totalPages > 1 && (
              <div className="sch-pagination">
                <div className="sch-pagination-info">
                  <i className="ri-information-line"></i>
                  <strong>{startIndex + 1}</strong> à <strong>{Math.min(startIndex + itemsPerPage, filteredAndSortedItems.length)}</strong> sur <strong>{filteredAndSortedItems.length}</strong>
                </div>
                <div className="sch-pagination-controls">
                  <button
                    className="sch-page-btn"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(1)}
                  >
                    <i className="ri-arrow-left-double-line"></i>
                  </button>
                  <button
                    className="sch-page-btn"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(currentPage - 1)}
                  >
                    <i className="ri-arrow-left-s-line"></i>
                  </button>

                  {[...Array(Math.min(5, totalPages))].map((_, index) => {
                    const pageNum = currentPage <= 3
                      ? index + 1
                      : currentPage >= totalPages - 2
                        ? totalPages - 4 + index
                        : currentPage - 2 + index;

                    if (pageNum > 0 && pageNum <= totalPages) {
                      return (
                        <button
                          key={pageNum}
                          className={`sch-page-btn ${currentPage === pageNum ? 'active' : ''}`}
                          onClick={() => setCurrentPage(pageNum)}
                        >
                          {pageNum}
                        </button>
                      );
                    }
                    return null;
                  })}

                  <button
                    className="sch-page-btn"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(currentPage + 1)}
                  >
                    <i className="ri-arrow-right-s-line"></i>
                  </button>
                  <button
                    className="sch-page-btn"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(totalPages)}
                  >
                    <i className="ri-arrow-right-double-line"></i>
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* ========== MODAL DÉTAIL ========== */}
      <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} size="lg" centered className="sch-modal">
        <div className="sch-modal-header sch-modal-header-blue">
          <div className="d-flex align-items-center gap-3">
            <div className="sch-modal-icon">
              <i className="ri-eye-line"></i>
            </div>
            <div>
              <h5 className="sch-modal-title mb-0">Détails du message</h5>
              <small className="sch-modal-subtitle">Informations complètes</small>
            </div>
          </div>
          <button className="sch-modal-close" onClick={() => setShowDetailModal(false)}>
            <i className="ri-close-line"></i>
          </button>
        </div>

        <Modal.Body className="sch-modal-body">
          {selectedMessage && (
            <Row className="g-3">
              <Col md={6}>
                <div className="sch-detail-item">
                  <div className="sch-detail-label">
                    <i className="ri-price-tag-3-line"></i>
                    Type
                  </div>
                  <span className={`sch-summary-badge ${selectedMessage.type === "SMS Pro" ? "amber" : "blue"}`}>
                    {selectedMessage.type}
                  </span>
                </div>
              </Col>
              <Col md={6}>
                <div className="sch-detail-item">
                  <div className="sch-detail-label">
                    <i className="ri-flag-line"></i>
                    Statut
                  </div>
                  <span
                    className="sch-status-badge"
                    style={getStatusColors(selectedMessage.statut)}
                  >
                    <i className={getStatusIcon(selectedMessage.statut)}></i>
                    {selectedMessage.statut}
                  </span>
                </div>
              </Col>
              <Col md={6}>
                <div className="sch-detail-item">
                  <div className="sch-detail-label">
                    <i className="ri-at-line"></i>
                    Sender ID
                  </div>
                  <span className="sch-badge sch-badge-info">{selectedMessage.sender_id}</span>
                </div>
              </Col>
              <Col md={6}>
                <div className="sch-detail-item">
                  <div className="sch-detail-label">
                    <i className="ri-calendar-line"></i>
                    Date de création
                  </div>
                  <div className="sch-detail-value">{selectedMessage.dateCreation}</div>
                </div>
              </Col>
              <Col md={12}>
                <div className="sch-detail-item">
                  <div className="sch-detail-label">
                    <i className="ri-user-line"></i>
                    Destinataire
                  </div>
                  <code className="sch-code">{selectedMessage.destinataires}</code>
                </div>
              </Col>
              <Col md={12}>
                <div className="sch-detail-item">
                  <div className="sch-detail-label">
                    <i className="ri-chat-quote-line"></i>
                    Contenu du message
                  </div>
                  <div className="sch-detail-message">{selectedMessage.texte}</div>
                </div>
              </Col>
              {selectedMessage.originalData && (
                <Col md={12}>
                  <div className="sch-detail-item">
                    <div className="sch-detail-label">
                      <i className="ri-code-line"></i>
                      Informations techniques
                    </div>
                    <div className="sch-detail-tech">
                      <div className="sch-detail-tech-row">
                        <span>ID</span>
                        <strong>{selectedMessage.originalData.id}</strong>
                      </div>
                      <div className="sch-detail-tech-row">
                        <span>Message original</span>
                        <strong>{selectedMessage.originalData.message}</strong>
                      </div>
                      {selectedMessage.originalData.processed_message && (
                        <div className="sch-detail-tech-row">
                          <span>Message traité</span>
                          <strong>{selectedMessage.originalData.processed_message}</strong>
                        </div>
                      )}
                      <div className="sch-detail-tech-row">
                        <span>Status API</span>
                        <strong>{selectedMessage.originalData.status}</strong>
                      </div>
                    </div>
                  </div>
                </Col>
              )}
            </Row>
          )}
        </Modal.Body>

        <div className="sch-modal-footer">
          <button className="sch-btn-cancel" onClick={() => setShowDetailModal(false)}>
            Fermer
          </button>
        </div>
      </Modal>

      {/* ========== MODAL SUPPRESSION ========== */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered className="sch-modal">
        <Modal.Body className="sch-delete-body">
          <div className="sch-delete-icon-wrapper">
            <div className="sch-delete-icon">
              <i className="ri-delete-bin-6-line"></i>
            </div>
          </div>

          <h4 className="sch-delete-title">Confirmer la suppression</h4>
          <p className="sch-delete-text">
            Êtes-vous sûr de vouloir supprimer ce message ? Cette action est irréversible.
          </p>

          {selectedMessage && (
            <div className="sch-delete-message">
              <div className="sch-delete-message-content">
                {selectedMessage.texte.substring(0, 100)}...
              </div>
              <div className="sch-delete-message-meta">
                <span><i className="ri-user-line"></i> {selectedMessage.destinataires}</span>
                <span><i className="ri-at-line"></i> {selectedMessage.sender_id}</span>
              </div>
            </div>
          )}

          <div className="sch-delete-actions">
            <button
              className="sch-btn-cancel sch-btn-flex"
              onClick={() => setShowDeleteModal(false)}
            >
              <i className="ri-close-line"></i>
              Annuler
            </button>
            <button
              className="sch-btn-danger sch-btn-flex"
              onClick={cancelScheduledMessage}
            >
              <i className="ri-delete-bin-line"></i>
              Supprimer
            </button>
          </div>
        </Modal.Body>
      </Modal>

      {/* ========== MODAL FORMULAIRE ========== */}
      <Modal show={showFormModal} onHide={() => setShowFormModal(false)} size="xl" scrollable centered className="sch-modal sch-modal-xl">
        <div className="sch-modal-header sch-modal-header-primary">
          <div className="d-flex align-items-center gap-3">
            <div className="sch-modal-icon sch-modal-icon-lg">
              <i className="ri-calendar-event-line"></i>
            </div>
            <div>
              <h5 className="sch-modal-title mb-0">
                {currentStep === "form" ? "Nouveau SMS programmé" : "Aperçu du message"}
              </h5>
              <small className="sch-modal-subtitle">
                {currentStep === "form" ? "Planifiez l'envoi de votre message" : "Vérifiez avant programmation"}
              </small>
            </div>
          </div>
          <button className="sch-modal-close" onClick={() => setShowFormModal(false)}>
            <i className="ri-close-line"></i>
          </button>
        </div>

        <Modal.Body className="sch-modal-body sch-modal-body-xl">
          {currentStep === "form" ? renderFormStep() : renderPreviewStep()}
        </Modal.Body>
      </Modal>

      <Footer />

      {/* ========== STYLES PREMIUM ========== */}
      <style>{`
        /* ========== GLOBAL ========== */
        .sch-page {
          background: #f8fafc;
          min-height: 100vh;
        }
        .sch-page * {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }

        /* ========== HEADER ========== */
        .sch-header {
          background: linear-gradient(135deg, #ffffff 0%, #f1f5f9 100%);
          border-radius: 16px;
          padding: 24px 28px;
          border: 1px solid #e2e8f0;
          box-shadow: 0 1px 3px rgba(0,0,0,0.03);
        }
        .sch-header-icon {
          width: 56px;
          height: 56px;
          border-radius: 14px;
          background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 26px;
          box-shadow: 0 8px 20px -6px rgba(59, 130, 246, 0.5);
        }
        .sch-header-title {
          font-weight: 700;
          color: #0f172a;
          font-size: 1.5rem;
          letter-spacing: -0.02em;
        }
        .sch-header-subtitle {
          color: #64748b;
          font-size: 0.875rem;
        }

        /* ========== ALERTS ========== */
        .sch-alert {
          display: flex;
          align-items: center;
          gap: 14px;
          padding: 16px 20px;
          border-radius: 12px;
        }
        .sch-alert-success {
          background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
          color: #065f46;
          border-left: 4px solid #10b981;
        }
        .sch-alert-success > i {
          color: #10b981;
          font-size: 1.5rem;
        }
        .sch-alert-close {
          background: none;
          border: none;
          color: inherit;
          opacity: 0.6;
          cursor: pointer;
          padding: 4px 8px;
          border-radius: 6px;
          font-size: 1rem;
        }
        .sch-alert-close:hover {
          opacity: 1;
          background: rgba(0,0,0,0.05);
        }

        /* ========== BUTTONS ========== */
        .sch-btn-primary, .sch-btn-secondary, .sch-btn-cancel, .sch-btn-danger {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 11px 20px;
          border-radius: 10px;
          font-weight: 600;
          font-size: 0.875rem;
          border: none;
          transition: all 0.2s;
          cursor: pointer;
          text-decoration: none;
        }
        .sch-btn-primary {
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
          color: white;
        }
        .sch-btn-primary:hover:not(:disabled) {
          background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
          transform: translateY(-1px);
          box-shadow: 0 8px 16px -4px rgba(59, 130, 246, 0.4);
        }
        .sch-btn-primary:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .sch-btn-secondary {
          background: white;
          border: 1px solid #e2e8f0;
          color: #334155;
        }
        .sch-btn-secondary:hover:not(:disabled) {
          background: #f8fafc;
          border-color: #cbd5e1;
        }
        .sch-btn-secondary:disabled {
          opacity: 0.6;
        }
        .sch-btn-cancel {
          background: white;
          border: 1px solid #e2e8f0;
          color: #334155;
        }
        .sch-btn-cancel:hover {
          background: #f1f5f9;
        }
        .sch-btn-danger {
          background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
          color: white;
        }
        .sch-btn-danger:hover {
          background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
          transform: translateY(-1px);
          box-shadow: 0 8px 16px -4px rgba(239, 68, 68, 0.4);
        }
        .sch-btn-flex {
          flex: 1;
        }

        /* ========== STATS ========== */
        .sch-stat-card {
          display: flex;
          align-items: center;
          gap: 14px;
          background: white;
          border-radius: 14px;
          padding: 18px 20px;
          border: 1px solid #e2e8f0;
          transition: all 0.25s;
        }
        .sch-stat-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px -6px rgba(0,0,0,0.08);
        }
        .sch-stat-icon-wrapper {
          width: 46px;
          height: 46px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          flex-shrink: 0;
        }
        .sch-stat-icon-blue  { background: #eff6ff; color: #3b82f6; }
        .sch-stat-icon-amber { background: #fef3c7; color: #f59e0b; }
        .sch-stat-icon-green { background: #d1fae5; color: #10b981; }
        .sch-stat-icon-red   { background: #fee2e2; color: #ef4444; }
        .sch-stat-content {
          min-width: 0;
          flex-grow: 1;
        }
        .sch-stat-label {
          font-size: 0.72rem;
          color: #64748b;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin-bottom: 3px;
        }
        .sch-stat-value {
          font-size: 1.5rem;
          font-weight: 700;
          color: #0f172a;
          letter-spacing: -0.02em;
          line-height: 1;
        }

        /* ========== FILTRES ========== */
        .sch-filters {
          background: white;
          border-radius: 14px;
          padding: 16px 20px;
          border: 1px solid #e2e8f0;
        }
        .sch-search-wrapper {
          position: relative;
          display: flex;
          align-items: center;
        }
        .sch-search-icon {
          position: absolute;
          left: 14px;
          color: #94a3b8;
          font-size: 1.05rem;
          pointer-events: none;
        }
        .sch-search-input {
          width: 100%;
          padding: 11px 40px 11px 42px;
          border: 1px solid #e2e8f0;
          border-radius: 10px;
          font-size: 0.875rem;
          background: #f8fafc;
          transition: all 0.2s;
          outline: none;
          font-family: inherit;
        }
        .sch-search-input:focus {
          background: white;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }
        .sch-search-clear {
          position: absolute;
          right: 8px;
          background: none;
          border: none;
          color: #94a3b8;
          font-size: 1.1rem;
          padding: 6px 10px;
          cursor: pointer;
          border-radius: 6px;
        }
        .sch-search-clear:hover {
          background: #fee2e2;
          color: #ef4444;
        }
        .sch-select {
          width: 100%;
          padding: 11px 14px;
          border: 1px solid #e2e8f0;
          border-radius: 10px;
          font-size: 0.875rem;
          font-weight: 500;
          background: #f8fafc;
          color: #334155;
          outline: none;
          cursor: pointer;
          font-family: inherit;
        }
        .sch-select:focus {
          background: white;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        /* ========== TABLE ========== */
        .sch-table-card {
          background: white;
          border-radius: 16px;
          border: 1px solid #e2e8f0;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0,0,0,0.03);
        }
        .sch-table {
          width: 100%;
          font-size: 0.875rem;
        }
        .sch-table thead th {
          background: #f8fafc;
          color: #475569;
          font-weight: 700;
          font-size: 0.7rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          padding: 16px;
          border-bottom: 1px solid #e2e8f0;
          white-space: nowrap;
        }
        .sch-table tbody td {
          padding: 16px;
          vertical-align: middle;
          border-bottom: 1px solid #f1f5f9;
        }
        .sch-row {
          transition: background-color 0.15s;
        }
        .sch-row:hover {
          background-color: #f8fafc;
        }
        .sch-sortable-header {
          cursor: pointer;
          user-select: none;
        }
        .sch-sortable-header:hover {
          color: #3b82f6;
        }
        .sch-sortable-content {
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }
        .sch-sort-icon {
          color: #3b82f6;
          font-size: 0.9rem;
        }

        .sch-message-cell {
          max-width: 320px;
        }
        .sch-message-text {
          font-weight: 500;
          color: #0f172a;
          font-size: 0.85rem;
          line-height: 1.4;
          margin-bottom: 4px;
          overflow: hidden;
          text-overflow: ellipsis;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
        }
        .sch-message-meta {
          font-size: 0.7rem;
          color: #94a3b8;
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .sch-code {
          font-size: 0.78rem;
          color: #1e40af;
          background: #eff6ff;
          padding: 4px 8px;
          border-radius: 5px;
          font-weight: 600;
          font-family: 'SF Mono', Menlo, Monaco, monospace;
        }

        .sch-badge {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          padding: 4px 10px;
          border-radius: 6px;
          font-size: 0.72rem;
          font-weight: 600;
          white-space: nowrap;
        }
        .sch-badge-info {
          background: #cffafe;
          color: #0891b2;
        }
        .sch-badge-light {
          background: #f1f5f9;
          color: #475569;
          border: 1px solid #e2e8f0;
        }

        .sch-status-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 5px 11px;
          border-radius: 6px;
          font-weight: 700;
          font-size: 0.72rem;
          white-space: nowrap;
        }

        .sch-actions {
          display: inline-flex;
          gap: 6px;
          justify-content: flex-end;
        }
        .sch-action-btn {
          width: 34px;
          height: 34px;
          border-radius: 8px;
          border: 1px solid #e2e8f0;
          background: white;
          color: #64748b;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.15s;
          font-size: 0.95rem;
        }
        .sch-action-view:hover {
          background: #06b6d4;
          border-color: #06b6d4;
          color: white;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px -2px rgba(6, 182, 212, 0.4);
        }
        .sch-action-delete:hover {
          background: #ef4444;
          border-color: #ef4444;
          color: white;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px -2px rgba(239, 68, 68, 0.4);
        }

        /* ========== LOADING / EMPTY / ERROR ========== */
        .sch-loading {
          padding: 80px 20px;
          text-align: center;
        }
        .sch-loading-spinner {
          width: 48px;
          height: 48px;
          border: 4px solid #e2e8f0;
          border-top-color: #3b82f6;
          border-radius: 50%;
          animation: sch-spin 0.8s linear infinite;
          margin: 0 auto 16px;
        }
        .sch-loading-text {
          color: #64748b;
          font-size: 0.875rem;
          font-weight: 500;
        }
        .sch-error-state {
          padding: 60px 20px;
          text-align: center;
          color: #991b1b;
        }
        .sch-error-state > i {
          font-size: 3rem;
          color: #ef4444;
          margin-bottom: 16px;
          display: block;
        }
        .sch-error-title {
          font-weight: 700;
          margin-bottom: 8px;
        }
        .sch-error-text {
          color: #64748b;
          margin-bottom: 20px;
        }
        .sch-empty-state {
          padding: 60px 20px;
          text-align: center;
        }
        .sch-empty-icon {
          width: 80px;
          height: 80px;
          border-radius: 50%;
          background: #f1f5f9;
          color: #94a3b8;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 36px;
          margin-bottom: 20px;
        }
        .sch-empty-title {
          color: #334155;
          font-weight: 700;
          margin-bottom: 8px;
        }
        .sch-empty-text {
          color: #94a3b8;
          margin-bottom: 20px;
        }
        .sch-btn-empty-reset {
          display: inline-flex;
          align-items: center;
          background: white;
          color: #3b82f6;
          border: 1px solid #bfdbfe;
          border-radius: 8px;
          font-weight: 600;
          padding: 8px 16px;
          cursor: pointer;
        }
        .sch-btn-empty-reset:hover {
          background: #3b82f6;
          color: white;
          border-color: #3b82f6;
        }

        /* ========== PAGINATION ========== */
        .sch-pagination {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 24px;
          background: #f8fafc;
          border-top: 1px solid #e2e8f0;
          flex-wrap: wrap;
          gap: 12px;
        }
        .sch-pagination-info {
          font-size: 0.8rem;
          color: #64748b;
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .sch-pagination-info strong {
          color: #0f172a;
        }
        .sch-pagination-controls {
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .sch-page-btn {
          min-width: 34px;
          height: 34px;
          padding: 0 10px;
          border-radius: 8px;
          border: 1px solid #e2e8f0;
          background: white;
          color: #475569;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.15s;
          font-size: 0.8rem;
          font-weight: 600;
        }
        .sch-page-btn:hover:not(:disabled) {
          background: #3b82f6;
          color: white;
          border-color: #3b82f6;
        }
        .sch-page-btn:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }
        .sch-page-btn.active {
          background: #3b82f6;
          color: white;
          border-color: #3b82f6;
        }

        /* ========== FORM CARD ========== */
        .sch-form-card {
          background: white;
          border-radius: 16px;
          border: 1px solid #e2e8f0;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0,0,0,0.03);
        }
        .sch-form-header {
          padding: 20px 24px;
          background: linear-gradient(180deg, #ffffff, #fafbfc);
          border-bottom: 1px solid #f1f5f9;
        }
        .sch-form-icon {
          width: 42px;
          height: 42px;
          border-radius: 10px;
          background: #eff6ff;
          color: #3b82f6;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
        }
        .sch-form-title {
          font-weight: 700;
          color: #0f172a;
          font-size: 0.95rem;
          margin: 0;
        }
        .sch-form-subtitle {
          color: #94a3b8;
          font-size: 0.75rem;
        }
        .sch-form-body {
          padding: 24px;
        }

        .sch-section {
          margin-bottom: 28px;
        }
        .sch-section:last-child {
          margin-bottom: 0;
        }
        .sch-label {
          display: flex;
          align-items: center;
          gap: 10px;
          font-weight: 700;
          color: #0f172a;
          font-size: 0.875rem;
          margin-bottom: 12px;
        }
        .sch-label-number {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: linear-gradient(135deg, #3b82f6, #8b5cf6);
          color: white;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 0.7rem;
          font-weight: 700;
        }
        .sch-required {
          color: #ef4444;
        }
        .sch-optional {
          color: #94a3b8;
          font-weight: 500;
        }

        /* Option cards */
        .sch-option-card {
          position: relative;
          display: flex;
          align-items: center;
          gap: 14px;
          padding: 16px;
          border-radius: 12px;
          border: 2px solid #e2e8f0;
          background: #f8fafc;
          cursor: pointer;
          transition: all 0.2s;
          margin: 0;
          height: 100%;
        }
        .sch-option-card:hover {
          background: white;
          border-color: #cbd5e1;
          transform: translateY(-2px);
          box-shadow: 0 8px 20px -6px rgba(0,0,0,0.06);
        }
        .sch-option-card.active {
          background: white;
          border-color: #3b82f6;
          box-shadow: 0 8px 20px -6px rgba(59, 130, 246, 0.2);
        }
        .sch-option-card input[type="radio"] {
          display: none;
        }
        .sch-option-icon {
          width: 44px;
          height: 44px;
          border-radius: 11px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          flex-shrink: 0;
        }
        .sch-option-icon-amber { background: #fef3c7; color: #f59e0b; }
        .sch-option-icon-blue  { background: #eff6ff; color: #3b82f6; }
        .sch-option-content {
          flex-grow: 1;
        }
        .sch-option-title {
          font-weight: 700;
          color: #0f172a;
          font-size: 0.875rem;
          margin-bottom: 2px;
        }
        .sch-option-desc {
          font-size: 0.75rem;
          color: #64748b;
        }
        .sch-option-check {
          color: #cbd5e1;
          font-size: 1.2rem;
          transition: all 0.2s;
          flex-shrink: 0;
        }
        .sch-option-card.active .sch-option-check {
          color: #3b82f6;
        }

        /* Textarea */
        .sch-textarea {
          width: 100%;
          padding: 14px 16px;
          border: 2px solid #e2e8f0;
          border-radius: 10px;
          font-size: 0.875rem;
          color: #0f172a;
          background: white;
          transition: all 0.2s;
          outline: none;
          font-family: inherit;
          line-height: 1.5;
          resize: vertical;
        }
        .sch-textarea:focus {
          border-color: #3b82f6;
          box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1);
        }
        .sch-textarea-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 10px;
          gap: 16px;
          flex-wrap: wrap;
        }
        .sch-hint {
          font-size: 0.75rem;
          color: #94a3b8;
          display: flex;
          align-items: center;
          gap: 5px;
        }
        .sch-hint i {
          color: #3b82f6;
        }
        .sch-counter {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 4px 10px;
          border-radius: 6px;
          font-size: 0.75rem;
          font-weight: 700;
        }
        .sch-counter-primary {
          background: #eff6ff;
          color: #3b82f6;
        }
        .sch-char-counter {
          display: flex;
          align-items: center;
          gap: 10px;
          flex-grow: 1;
          max-width: 300px;
        }
        .sch-char-progress {
          flex-grow: 1;
          height: 6px;
          background: #f1f5f9;
          border-radius: 10px;
          overflow: hidden;
        }
        .sch-char-progress-bar {
          height: 100%;
          background: linear-gradient(90deg, #10b981, #34d399);
          border-radius: 10px;
          transition: width 0.3s ease;
        }
        .sch-char-progress-bar.warning {
          background: linear-gradient(90deg, #f59e0b, #ef4444);
        }
        .sch-char-text {
          font-size: 0.75rem;
          color: #64748b;
          font-weight: 600;
          font-variant-numeric: tabular-nums;
          white-space: nowrap;
        }
        .sch-char-text.warning {
          color: #f59e0b;
        }
        .sch-warning {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          font-size: 0.75rem;
          color: #f59e0b;
          font-weight: 600;
        }

        /* Inputs */
        .sch-input-wrapper {
          position: relative;
          display: flex;
          align-items: center;
        }
        .sch-input-icon {
          position: absolute;
          left: 16px;
          color: #94a3b8;
          font-size: 1.05rem;
          pointer-events: none;
          z-index: 5;
        }
        .sch-input, .sch-input-full {
          width: 100%;
          padding: 12px 16px 12px 46px;
          border: 2px solid #e2e8f0;
          border-radius: 10px;
          font-size: 0.875rem;
          color: #0f172a;
          background: white;
          transition: all 0.2s;
          outline: none;
          font-family: inherit;
        }
        .sch-input-full {
          padding-left: 16px;
        }
        .sch-input:focus, .sch-input-full:focus {
          border-color: #3b82f6;
          box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1);
        }

        /* Options grid */
        .sch-options-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
          gap: 12px;
        }
        .sch-option-checkbox {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 14px;
          border-radius: 10px;
          border: 2px solid #e2e8f0;
          background: white;
          cursor: pointer;
          transition: all 0.2s;
          margin: 0;
        }
        .sch-option-checkbox:hover {
          border-color: #cbd5e1;
          background: #f8fafc;
        }
        .sch-option-checkbox.active {
          border-color: #3b82f6;
          background: #eff6ff;
        }
        .sch-option-checkbox input[type="checkbox"] {
          display: none;
        }
        .sch-option-checkbox-icon {
          width: 36px;
          height: 36px;
          border-radius: 9px;
          background: #f1f5f9;
          color: #64748b;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 16px;
          flex-shrink: 0;
        }
        .sch-option-checkbox.active .sch-option-checkbox-icon {
          background: #3b82f6;
          color: white;
        }
        .sch-option-checkbox-title {
          font-weight: 600;
          color: #0f172a;
          font-size: 0.82rem;
        }
        .sch-option-checkbox-desc {
          font-size: 0.7rem;
          color: #94a3b8;
        }

        /* ========== SIDEBAR SUMMARY ========== */
        .sch-sidebar {
          position: sticky;
        }
        .sch-summary-card {
          background: white;
          border-radius: 16px;
          border: 1px solid #e2e8f0;
          overflow: hidden;
          box-shadow: 0 4px 12px -4px rgba(0,0,0,0.05);
        }
        .sch-summary-header {
          padding: 20px 24px;
          background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
          color: white;
        }
        .sch-summary-icon {
          width: 40px;
          height: 40px;
          border-radius: 10px;
          background: rgba(59, 130, 246, 0.2);
          color: #60a5fa;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 18px;
        }
        .sch-summary-title {
          font-weight: 700;
          color: white;
          margin: 0;
          font-size: 0.95rem;
        }
        .sch-summary-subtitle {
          color: #94a3b8;
          font-size: 0.72rem;
        }
        .sch-summary-body {
          padding: 20px 24px;
        }
        .sch-summary-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px 0;
          border-bottom: 1px solid #f1f5f9;
        }
        .sch-summary-item:last-child {
          border-bottom: none;
        }
        .sch-summary-label {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-size: 0.8rem;
          color: #64748b;
          font-weight: 500;
        }
        .sch-summary-label i {
          color: #94a3b8;
          font-size: 0.95rem;
        }
        .sch-summary-value {
          font-weight: 700;
          color: #0f172a;
          font-size: 0.875rem;
        }
        .sch-summary-value-highlight {
          color: #3b82f6;
          font-size: 1.1rem;
        }
        .sch-summary-badge {
          display: inline-block;
          padding: 4px 10px;
          border-radius: 6px;
          font-size: 0.7rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.03em;
        }
        .sch-summary-badge.blue  { background: #eff6ff; color: #3b82f6; }
        .sch-summary-badge.amber { background: #fef3c7; color: #d97706; }
        .sch-summary-divider {
          height: 1px;
          background: linear-gradient(90deg, transparent, #e2e8f0, transparent);
          margin: 16px 0;
        }
        .sch-summary-date {
          padding: 4px 0;
        }
        .sch-summary-date-label {
          font-size: 0.75rem;
          color: #64748b;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 6px;
          margin-bottom: 8px;
        }
        .sch-summary-date-label i {
          color: #3b82f6;
        }
        .sch-summary-date-value {
          background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
          border-radius: 10px;
          padding: 12px;
          text-align: center;
          border: 1px solid #bfdbfe;
        }
        .sch-summary-date-day {
          font-size: 0.875rem;
          font-weight: 700;
          color: #1e40af;
        }
        .sch-summary-date-time {
          font-size: 0.8rem;
          color: #3b82f6;
          margin-top: 2px;
        }
        .sch-summary-date-empty {
          background: #f8fafc;
          border-radius: 10px;
          padding: 12px;
          text-align: center;
          color: #94a3b8;
          font-size: 0.82rem;
          border: 1px dashed #cbd5e1;
        }
        .sch-summary-cost {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .sch-summary-cost-label {
          font-size: 0.8rem;
          color: #64748b;
          font-weight: 600;
        }
        .sch-summary-cost-value {
          font-size: 1.4rem;
          font-weight: 700;
          color: #10b981;
        }
        .sch-summary-cost-value small {
          font-size: 0.75rem;
          color: #94a3b8;
          font-weight: 600;
        }
        .sch-summary-footer {
          padding: 20px 24px;
          border-top: 1px solid #f1f5f9;
          background: #fafbfc;
          display: flex;
          flex-direction: column;
          gap: 10px;
        }
        .sch-summary-hint {
          text-align: center;
          font-size: 0.7rem;
          color: #94a3b8;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 5px;
          margin-top: 4px;
        }
        .sch-summary-hint i {
          color: #10b981;
        }

        /* ========== PREVIEW ========== */
        .sch-preview {
          padding: 8px;
        }
        .sch-preview-header {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 20px 24px;
          background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
          border-radius: 14px;
          margin-bottom: 24px;
          border: 1px solid #bfdbfe;
        }
        .sch-preview-header-icon {
          width: 52px;
          height: 52px;
          border-radius: 13px;
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 22px;
          box-shadow: 0 8px 16px -4px rgba(59, 130, 246, 0.4);
        }
        .sch-preview-header-title {
          font-weight: 700;
          color: #0f172a;
          font-size: 1.15rem;
        }
        .sch-preview-header-subtitle {
          color: #64748b;
          font-size: 0.82rem;
        }

        .sch-preview-stat {
          background: white;
          border-radius: 12px;
          padding: 18px;
          text-align: center;
          border: 1px solid #e2e8f0;
          transition: all 0.2s;
        }
        .sch-preview-stat:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px -6px rgba(0,0,0,0.06);
        }
        .sch-preview-stat-icon {
          width: 44px;
          height: 44px;
          border-radius: 11px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          margin-bottom: 10px;
        }
        .sch-preview-stat-icon-blue  { background: #eff6ff; color: #3b82f6; }
        .sch-preview-stat-icon-green { background: #d1fae5; color: #10b981; }
        .sch-preview-stat-icon-amber { background: #fef3c7; color: #f59e0b; }
        .sch-preview-stat-value {
          font-weight: 700;
          color: #0f172a;
          font-size: 1.1rem;
        }
        .sch-preview-stat-label {
          font-size: 0.72rem;
          color: #94a3b8;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin-top: 4px;
        }

        .sch-preview-section {
          background: white;
          border-radius: 14px;
          padding: 20px 24px;
          border: 1px solid #e2e8f0;
          margin-bottom: 16px;
        }
        .sch-preview-section-header {
          display: flex;
          align-items: center;
          gap: 8px;
          font-weight: 700;
          color: #0f172a;
          font-size: 0.9rem;
          margin-bottom: 16px;
          padding-bottom: 12px;
          border-bottom: 1px solid #f1f5f9;
        }
        .sch-preview-section-header i {
          color: #3b82f6;
          font-size: 1.1rem;
        }
        .sch-preview-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 10px 14px;
          background: #f8fafc;
          border-radius: 8px;
        }
        .sch-preview-row-label {
          font-size: 0.8rem;
          color: #64748b;
          font-weight: 500;
        }
        .sch-preview-row-value {
          font-weight: 700;
          color: #0f172a;
          font-size: 0.875rem;
        }

        .sch-preview-option {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 14px;
          background: #f0fdf4;
          border-radius: 8px;
          font-size: 0.8rem;
          color: #065f46;
          font-weight: 600;
        }
        .sch-preview-option i {
          color: #10b981;
        }

        .sch-preview-empty {
          text-align: center;
          padding: 20px;
          color: #94a3b8;
          font-size: 0.82rem;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
        }

        .sch-preview-recipients {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
          gap: 8px;
        }
        .sch-preview-recipient {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 12px;
          background: #f8fafc;
          border-radius: 8px;
          font-size: 0.82rem;
          border: 1px solid #f1f5f9;
        }
        .sch-preview-recipient i {
          color: #3b82f6;
          font-size: 0.9rem;
        }
        .sch-preview-recipient code {
          color: #0f172a;
          font-family: 'SF Mono', Menlo, Monaco, monospace;
          font-weight: 600;
          font-size: 0.78rem;
        }

        .sch-preview-message {
          background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
          border-radius: 12px;
          padding: 20px;
          color: white;
          position: relative;
          min-height: 100px;
        }
        .sch-preview-message-label {
          font-size: 0.65rem;
          color: #94a3b8;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          font-weight: 700;
          margin-bottom: 12px;
        }
        .sch-preview-message-content {
          font-size: 0.95rem;
          line-height: 1.6;
          white-space: pre-wrap;
          word-break: break-word;
        }
        .sch-preview-message-footer {
          position: absolute;
          bottom: 10px;
          right: 16px;
          font-size: 0.7rem;
          color: #94a3b8;
          font-variant-numeric: tabular-nums;
        }

        .sch-preview-actions {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 12px;
          padding: 16px 20px;
          background: #f8fafc;
          border-radius: 12px;
          flex-wrap: wrap;
        }
        .sch-preview-actions-info {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 0.82rem;
          color: #64748b;
          font-weight: 600;
        }
        .sch-preview-actions-info i {
          color: #10b981;
        }
        .sch-preview-actions-buttons {
          display: flex;
          gap: 10px;
        }

        /* ========== MODAL ========== */
        .sch-modal .modal-content {
          border: none;
          border-radius: 16px;
          overflow: hidden;
        }
        .sch-modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px 24px;
          color: white;
        }
        .sch-modal-header-blue {
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
        }
        .sch-modal-header-primary {
          background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
        }
        .sch-modal-icon {
          width: 44px;
          height: 44px;
          border-radius: 10px;
          background: rgba(255,255,255,0.2);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
        }
        .sch-modal-icon-lg {
          width: 52px;
          height: 52px;
          font-size: 24px;
        }
        .sch-modal-title {
          font-weight: 700;
          letter-spacing: -0.01em;
        }
        .sch-modal-subtitle {
          color: rgba(255,255,255,0.8);
          font-size: 0.8rem;
        }
        .sch-modal-close {
          background: rgba(255,255,255,0.1);
          border: none;
          color: white;
          width: 36px;
          height: 36px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s;
          font-size: 1.1rem;
        }
        .sch-modal-close:hover {
          background: rgba(255,255,255,0.2);
          transform: rotate(90deg);
        }
        .sch-modal-body {
          padding: 24px;
          background: #f8fafc;
        }
        .sch-modal-body-xl {
          max-height: 75vh;
          overflow-y: auto;
        }
        .sch-modal-footer {
          padding: 16px 24px;
          background: white;
          border-top: 1px solid #e2e8f0;
          display: flex;
          justify-content: flex-end;
        }

        /* Détails */
        .sch-detail-item {
          background: white;
          padding: 14px 16px;
          border-radius: 10px;
          border: 1px solid #e2e8f0;
          height: 100%;
        }
        .sch-detail-label {
          font-size: 0.7rem;
          color: #64748b;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          display: flex;
          align-items: center;
          gap: 6px;
          margin-bottom: 8px;
        }
        .sch-detail-label i {
          color: #3b82f6;
          font-size: 0.9rem;
        }
        .sch-detail-value {
          font-weight: 600;
          color: #0f172a;
          font-size: 0.875rem;
        }
        .sch-detail-message {
          background: #f8fafc;
          border-left: 3px solid #3b82f6;
          padding: 12px 14px;
          border-radius: 8px;
          font-size: 0.85rem;
          color: #334155;
          line-height: 1.5;
          white-space: pre-wrap;
          word-break: break-word;
        }
        .sch-detail-tech {
          background: #f8fafc;
          border-radius: 8px;
          padding: 4px;
        }
        .sch-detail-tech-row {
          display: flex;
          justify-content: space-between;
          gap: 12px;
          padding: 8px 12px;
          font-size: 0.78rem;
          color: #64748b;
        }
        .sch-detail-tech-row:not(:last-child) {
          border-bottom: 1px solid #f1f5f9;
        }
        .sch-detail-tech-row strong {
          color: #0f172a;
          text-align: right;
          word-break: break-word;
        }

        /* Delete modal */
        .sch-delete-body {
          padding: 32px 24px 24px;
          text-align: center;
        }
        .sch-delete-icon-wrapper {
          margin-bottom: 20px;
        }
        .sch-delete-icon {
          width: 80px;
          height: 80px;
          border-radius: 50%;
          background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
          color: #dc2626;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 36px;
          box-shadow: 0 12px 24px -8px rgba(220, 38, 38, 0.3);
        }
        .sch-delete-title {
          font-weight: 700;
          color: #0f172a;
          margin-bottom: 12px;
          font-size: 1.25rem;
        }
        .sch-delete-text {
          color: #64748b;
          margin-bottom: 20px;
          font-size: 0.875rem;
        }
        .sch-delete-message {
          background: #f8fafc;
          border-radius: 12px;
          padding: 16px 20px;
          margin-bottom: 20px;
          border: 1px solid #e2e8f0;
          text-align: left;
        }
        .sch-delete-message-content {
          font-size: 0.85rem;
          color: #334155;
          line-height: 1.4;
          margin-bottom: 10px;
        }
        .sch-delete-message-meta {
          display: flex;
          gap: 16px;
          font-size: 0.72rem;
          color: #94a3b8;
        }
        .sch-delete-message-meta span {
          display: inline-flex;
          align-items: center;
          gap: 4px;
        }
        .sch-delete-actions {
          display: flex;
          gap: 10px;
        }

        /* ========== ANIMATIONS ========== */
        .spinning {
          animation: sch-spin 1s linear infinite;
          display: inline-block;
        }
        @keyframes sch-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        /* ========== RESPONSIVE ========== */
        @media (max-width: 992px) {
          .sch-preview-actions {
            flex-direction: column;
            align-items: stretch;
          }
          .sch-preview-actions-buttons {
            justify-content: stretch;
          }
          .sch-preview-actions-buttons > * {
            flex: 1;
          }
        }
        @media (max-width: 768px) {
          .sch-header {
            padding: 20px;
          }
          .sch-header-title {
            font-size: 1.2rem;
          }
          .sch-pagination {
            flex-direction: column;
          }
          .sch-stat-value {
            font-size: 1.25rem;
          }
          .sch-form-body {
            padding: 16px;
          }
          .sch-modal-body {
            padding: 16px;
          }
          .sch-delete-actions {
            flex-direction: column;
          }
          .sch-options-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
}